import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Zap,
  FileText,
  PoundSterling,
  ArrowRight,
  Smartphone,
  Wrench,
  ShieldCheck,
  BookOpen,
  Clock,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

export interface TradePainPoint {
  heading: string;
  body: string;
}

export interface TradeFeature {
  icon: React.ElementType;
  title: string;
  description: string;
}

export interface TradeFaq {
  q: string;
  a: string;
}

export interface TradeRelatedTrade {
  href: string;
  label: string;
  description: string;
}

export interface TradePageData {
  slug: string;
  tradeName: string;
  breadcrumbLabel: string;
  seoTitle: string;
  seoDescription: string;
  schemaName: string;
  schemaDescription: string;
  h1: string;
  heroSubtitle: string;
  heroSubtitle2?: string;
  painPointsHeading: string;
  painPoints: TradePainPoint[];
  featuresHeading: string;
  featuresSubheading: string;
  features: TradeFeature[];
  faqs: TradeFaq[];
  ctaHeading: string;
  ctaBody: string;
  relatedLinks: { href: string; label: string }[];
  relatedTrades: TradeRelatedTrade[];
}

const ELECTRICIANS: TradePageData = {
  slug: "quoting-software-for-electricians",
  tradeName: "electricians",
  breadcrumbLabel: "Quoting Software for Electricians",
  seoTitle: "Quoting Software for Electricians UK | The Proper Tool",
  seoDescription:
    "Quoting software built for electricians in the UK. Generate accurate, itemised electrical quotes from a description or site photos in under 10 minutes. NICEIC-compliant notes included. From £29/mo.",
  schemaName: "The Proper Tool | Quoting Software for Electricians",
  schemaDescription:
    "Quoting and estimating software built for electricians in the UK. Generate accurate, professional quotes from drawings, photos or a description in under 10 minutes.",
  h1: "Quoting software for electricians UK",
  heroSubtitle:
    "Stop pricing jobs from memory. The Proper Tool generates a fully itemised electrical quote (first fix, second fix, consumer units, and everything in between) in under 10 minutes.",
  heroSubtitle2:
    "Add NICEIC compliance notes, Part P reference, and your certification details automatically. Your client gets a professional PDF that stands up to scrutiny.",
  painPointsHeading: "The electrician's quoting problem",
  painPoints: [
    {
      heading: "Multi-phase jobs are complex to price",
      body: "A rewire or new build involves first fix, second fix, consumer unit, testing, and certification. Miss a phase in the scope and you're absorbing the cost later. The Proper Tool prices the full electrical scope from a single description: every phase, every circuit.",
    },
    {
      heading: "NICEIC and Part P compliance notes take time",
      body: "Clients increasingly expect to see certification references and compliance notes on their quote. Writing these out by hand for every job is slow. The Proper Tool lets you set up your NICEIC or NAPIT details once and include them on every quote automatically.",
    },
    {
      heading: "Material costs move constantly",
      body: "Cable, CU, accessories: prices from your wholesaler change regularly. The Proper Tool's UK cost library carries current market rates, and you can override any line item with your own supplier price. Your quotes always reflect what the job will actually cost.",
    },
    {
      heading: "Time writing quotes is time off the tools",
      body: "Every hour spent building a spreadsheet quote is an hour you're not earning. The Proper Tool cuts quote time from two hours to under ten minutes so you can price more jobs and spend more time on site.",
    },
  ],
  featuresHeading: "Everything an electrician needs to quote faster",
  featuresSubheading:
    "Built for UK residential and light commercial electrical work, not a generic tool adapted for every trade.",
  features: [
    {
      icon: Zap,
      title: "Electrical scope in under 10 minutes",
      description:
        "Describe the job (rewire, new build, EV charger install, consumer unit upgrade) and the AI generates a fully itemised electrical scope with UK market rates applied.",
    },
    {
      icon: ShieldCheck,
      title: "Compliance notes built in",
      description:
        "Add your NICEIC, NAPIT, or Part P registration details once. The Proper Tool includes them on every quote PDF so clients see your credentials without you typing them each time.",
    },
    {
      icon: PoundSterling,
      title: "UK electrical rates across all phases",
      description:
        "First fix wiring, second fix accessories, consumer units, testing and certification: all priced from a UK cost library with Low, Typical, and High rate bands. Override any rate with your own.",
    },
    {
      icon: FileText,
      title: "Itemised PDF your clients trust",
      description:
        "Every quote exports as a branded PDF with your company details, a full scope of works, and a line-by-line breakdown. Clients can see exactly what they're paying for.",
    },
    {
      icon: Smartphone,
      title: "Quote on-site from your phone",
      description:
        "Walk the job, describe what you see, and have a quote PDF in the client's inbox before you've driven home. No laptop, no spreadsheet.",
    },
    {
      icon: Wrench,
      title: "Override rates with your own",
      description:
        "Set your own rate on any task in the cost library, whether it's your cable supplier price or your labour rate, and it always takes priority on future quotes.",
    },
  ],
  faqs: [
    {
      q: "What is the best quoting software for electricians in the UK?",
      a: "For UK electricians, The Proper Tool is purpose-built: a UK cost library covering all phases of electrical work, AI quote generation from a description or site photos, NICEIC and Part P compliance note support, and branded PDF export. Plans start at £29/month with a 14-day free trial offering one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, with no credit card required.",
    },
    {
      q: "Can I include NICEIC and Part P details on my electrical quotes?",
      a: "Yes. The Proper Tool lets you set up your certification details (NICEIC, NAPIT, Part P registration) once in your profile and include them automatically on every quote PDF you send. Clients see your credentials without you adding them by hand each time.",
    },
    {
      q: "Does the quoting software cover all phases of electrical work?",
      a: "Yes. The Proper Tool covers first fix wiring, second fix accessories, consumer unit installation and upgrades, EV charger installation, testing and inspection, and certification. Describe the job and the AI generates the itemised scope across all phases.",
    },
    {
      q: "How does electrical estimating software handle material cost volatility?",
      a: "The Proper Tool prices every line item from a UK cost library with current market rates. You can override any rate with your own wholesaler price, so your quotes always reflect what you're actually paying for cable, accessories, and consumer units. Override once and the rate applies to all future quotes for that item.",
    },
    {
      q: "Is there a free trial for electrician quoting software?",
      a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start at £29/month.",
    },
  ],
  ctaHeading: "Ready to quote faster?",
  ctaBody:
    "Join electricians using The Proper Tool to generate professional quotes in minutes and win more jobs.",
  relatedLinks: [
    { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
    { href: "/quoting-software-for-plumbers", label: "Quoting Software for Plumbers" },
    { href: "/quoting-software-for-roofers", label: "Quoting Software for Roofers" },
    { href: "/ai-quoting-software", label: "AI Quoting Software for UK Builders" },
    { href: "/auto-quote", label: "Auto Quote: under 10 minutes" },
    { href: "/pricing", label: "Pricing & Plans" },
  ],
  relatedTrades: [
    {
      href: "/quoting-software-for-plumbers",
      label: "Quoting software for plumbers",
      description: "Bathroom fits, boiler swaps, heating systems, and drainage. Fully itemised plumbing quotes in under 10 minutes.",
    },
    {
      href: "/quoting-software-for-roofers",
      label: "Quoting software for roofers",
      description: "Flat and pitched roofs, scaffold prelims, and material uplifts. Accurate roofing quotes from a description or site photo.",
    },
    {
      href: "/tradesman-quoting-tool",
      label: "Tradesman quoting tool",
      description: "The complete quoting toolkit for UK tradespeople across all trades. One platform, every job type.",
    },
  ],
};

const PLUMBERS: TradePageData = {
  slug: "quoting-software-for-plumbers",
  tradeName: "plumbers",
  breadcrumbLabel: "Quoting Software for Plumbers",
  seoTitle: "Quoting Software for Plumbers UK | The Proper Tool",
  seoDescription:
    "Quoting software built for plumbers in the UK. Generate accurate, itemised plumbing quotes for bathrooms, heating systems, and drainage in under 10 minutes. Gas Safe reference included. From £29/mo.",
  schemaName: "The Proper Tool | Quoting Software for Plumbers",
  schemaDescription:
    "Quoting and estimating software built for plumbers in the UK. Generate accurate, professional quotes from drawings, photos or a description in under 10 minutes.",
  h1: "Quoting software for plumbers UK",
  heroSubtitle:
    "Bathroom fits, heating systems, unvented cylinders, drainage: The Proper Tool generates a fully itemised plumbing quote in under 10 minutes so you can price more jobs and spend more time on the tools.",
  heroSubtitle2:
    "Include your Gas Safe registration number and relevant compliance references automatically. Every quote PDF looks professional and stands up to scrutiny.",
  painPointsHeading: "The plumber's quoting problem",
  painPoints: [
    {
      heading: "Gas Safe and Part P compliance takes extra work",
      body: "Clients hiring plumbers for gas or unvented cylinder work need to see your Gas Safe or OFTEC registration details. Adding those by hand on every quote is error-prone and slow. The Proper Tool includes your certification details automatically on every quote you send.",
    },
    {
      heading: "Bathroom fits have serious scope creep",
      body: "A bathroom quote involves supply and waste pipework, tiling prep, fixtures, and often structural work behind the scenes. Miss an item and you're doing it for free. The Proper Tool prompts for the full bathroom scope so nothing falls through the cracks.",
    },
    {
      heading: "Heating system quotes are hard to itemise",
      body: "Boiler swaps, system flushes, radiator circuits, controls, and commissioning all need pricing separately. The Proper Tool prices each component individually from a UK cost library, giving your client a transparent breakdown they can actually read.",
    },
    {
      heading: "Pricing jobs takes evenings you don't have",
      body: "Most plumbers build quotes in their own time after a full day on site. The Proper Tool cuts that to under ten minutes, so you can send a professional quote from your phone before you leave the job.",
    },
  ],
  featuresHeading: "Everything a plumber needs to quote faster",
  featuresSubheading:
    "Built for UK plumbing and heating: bathrooms, boiler swaps, drainage, and everything in between.",
  features: [
    {
      icon: Zap,
      title: "Full plumbing scope in under 10 minutes",
      description:
        "Describe the job (bathroom refit, boiler swap, heating system, drainage) and the AI generates a fully itemised scope with UK market rates for every line item.",
    },
    {
      icon: ShieldCheck,
      title: "Gas Safe and compliance details built in",
      description:
        "Add your Gas Safe, OFTEC, or other certification details once. The Proper Tool includes them automatically on every quote PDF so clients see your credentials without manual entry.",
    },
    {
      icon: BookOpen,
      title: "Bathroom and heating cost library",
      description:
        "Sanitary ware supply and fit, unvented cylinders, radiator circuits, boiler installation, pipework: all covered with UK Low, Typical, and High rate bands.",
    },
    {
      icon: FileText,
      title: "Itemised PDF your clients trust",
      description:
        "Every quote exports as a branded PDF with your company details, a full itemised scope, and line-by-line pricing. Clients can see exactly what they're paying for, and so can you.",
    },
    {
      icon: Smartphone,
      title: "Quote on-site from your phone",
      description:
        "Walk the bathroom, describe what you see, and send a professional PDF before you've driven home. No laptop, no spreadsheet, no evening lost to admin.",
    },
    {
      icon: Wrench,
      title: "Override rates with your own",
      description:
        "Set your own labour rate and supplier prices on any item in the cost library. Your rates always take priority on future quotes so you price the way your business actually works.",
    },
  ],
  faqs: [
    {
      q: "What is the best quoting software for plumbers in the UK?",
      a: "For UK plumbers, The Proper Tool is purpose-built: a UK cost library covering plumbing and heating trades, AI quote generation from a description or site photos, Gas Safe compliance note support, and branded PDF export. Plans start at £29/month with a 14-day free trial offering one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, with no credit card required.",
    },
    {
      q: "Can I include my Gas Safe registration on plumbing quotes?",
      a: "Yes. You can add your Gas Safe or OFTEC registration number to your profile and The Proper Tool will include it automatically on every quote PDF you send. No manual entry each time.",
    },
    {
      q: "Does the plumbing quoting software cover bathrooms and heating systems?",
      a: "Yes. The Proper Tool covers bathroom supply and fit (sanitary ware, tiling prep, waste and supply pipework), unvented cylinder installation, boiler swaps, radiator circuits, system flush and commission, and drainage. Describe the job and the AI generates the itemised scope.",
    },
    {
      q: "How does a plumbing estimating tool handle scope creep on bathroom fits?",
      a: "The Proper Tool generates a fully itemised scope from your description, covering every component: supply pipework, waste runs, fixtures, tiling prep, and any structural allowances. You review every line before sending, so you catch missing items before the job starts rather than absorbing the cost on site.",
    },
    {
      q: "Is there a free trial for plumbing quoting software?",
      a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start at £29/month.",
    },
  ],
  ctaHeading: "Ready to quote faster?",
  ctaBody:
    "Join plumbers using The Proper Tool to generate professional quotes in minutes and win more jobs.",
  relatedLinks: [
    { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
    { href: "/quoting-software-for-electricians", label: "Quoting Software for Electricians" },
    { href: "/quoting-software-for-roofers", label: "Quoting Software for Roofers" },
    { href: "/ai-quoting-software", label: "AI Quoting Software for UK Builders" },
    { href: "/auto-quote", label: "Auto Quote: under 10 minutes" },
    { href: "/pricing", label: "Pricing & Plans" },
  ],
  relatedTrades: [
    {
      href: "/quoting-software-for-electricians",
      label: "Quoting software for electricians",
      description: "First fix, second fix, consumer units, and compliance notes. Full electrical scopes in under 10 minutes.",
    },
    {
      href: "/quoting-software-for-roofers",
      label: "Quoting software for roofers",
      description: "Flat and pitched roofs, scaffold prelims, and material uplifts. Accurate roofing quotes from a description or site photo.",
    },
    {
      href: "/tradesman-quoting-tool",
      label: "Tradesman quoting tool",
      description: "The complete quoting toolkit for UK tradespeople across all trades. One platform, every job type.",
    },
  ],
};

const ROOFERS: TradePageData = {
  slug: "quoting-software-for-roofers",
  tradeName: "roofers",
  breadcrumbLabel: "Quoting Software for Roofers",
  seoTitle: "Quoting Software for Roofers UK | The Proper Tool",
  seoDescription:
    "Quoting software built for roofers in the UK. Generate accurate, itemised roofing quotes covering flat and pitched roofs, scaffold prelims, and material uplifts in under 10 minutes. From £29/mo.",
  schemaName: "The Proper Tool | Quoting Software for Roofers",
  schemaDescription:
    "Quoting and estimating software built for roofers in the UK. Generate accurate, professional quotes from drawings, photos or a description in under 10 minutes.",
  h1: "Quoting software for roofers UK",
  heroSubtitle:
    "Flat roofs, pitched slate, GRP, scaffold prelims: The Proper Tool generates a fully itemised roofing quote in under 10 minutes so you can price more jobs without spending evenings on spreadsheets.",
  heroSubtitle2:
    "Material uplifts, lead flashings, fascia and soffit: every line is priced from a UK cost library with rate bands you can override with your own supplier prices.",
  painPointsHeading: "The roofer's quoting problem",
  painPoints: [
    {
      heading: "Material costs are volatile and hard to predict",
      body: "Slate, concrete tiles, felt, GRP resin, EPDM: prices shift with supply chains. Under-price the materials and you're losing money before you've laid a tile. The Proper Tool carries current UK market rates for all common roofing materials, and you can override any rate with your current supplier price.",
    },
    {
      heading: "Scaffold prelims are often forgotten",
      body: "Scaffold hire is a significant cost on most roofing jobs, but it often gets missed or underpriced in quotes. The Proper Tool automatically includes a scaffold prelim line when the job requires access, so you're not absorbing that cost after the fact.",
    },
    {
      heading: "Flat and pitched roof scopes are completely different",
      body: "A flat GRP or EPDM quote looks nothing like a pitched slate or tile re-roof. Generic quoting tools don't understand the difference. The Proper Tool's AI generates the correct scope for each roof type, including decking, insulation, upstands, and drainage for flat roofs.",
    },
    {
      heading: "Quote writing takes time away from the site",
      body: "Most roofers price jobs in the evening after a full day on the roof. The Proper Tool cuts that from two hours to under ten minutes, so you can send a professional PDF from your phone before you've left the driveway.",
    },
  ],
  featuresHeading: "Everything a roofer needs to quote faster",
  featuresSubheading:
    "Built for UK roofing: flat and pitched, residential and light commercial, new build and repair.",
  features: [
    {
      icon: Zap,
      title: "Full roofing scope in under 10 minutes",
      description:
        "Describe the job (re-roof, flat GRP, EPDM, ridge and hip replacement, valley repair) and the AI generates a fully itemised scope with UK market rates for every line item.",
    },
    {
      icon: BookOpen,
      title: "Flat and pitched roof cost library",
      description:
        "Slate, concrete tile, GRP, EPDM, felt, lead flashings, fascia and soffit, guttering, scaffold: all priced from a UK cost library with Low, Typical, and High rate bands.",
    },
    {
      icon: Clock,
      title: "Scaffold prelims included automatically",
      description:
        "The Proper Tool includes a scaffold hire and erection line when the job requires access equipment, so you never forget to price it and never absorb that cost yourself.",
    },
    {
      icon: FileText,
      title: "Itemised PDF your clients trust",
      description:
        "Every quote exports as a branded PDF with your company details, a full scope of works, and line-by-line pricing. Clients see exactly what the job involves with no round-number surprises.",
    },
    {
      icon: Smartphone,
      title: "Quote on-site from your phone",
      description:
        "Walk the roof, describe what you see or take a photo, and send a professional quote PDF before you've left the property. No laptop, no spreadsheet, no lost evenings.",
    },
    {
      icon: PoundSterling,
      title: "Override with your own material prices",
      description:
        "Set your own supplier price on any material in the cost library (slate, tile, felt, EPDM) and it always takes priority on future quotes so your margins stay protected.",
    },
  ],
  faqs: [
    {
      q: "What is the best quoting software for roofers in the UK?",
      a: "For UK roofers, The Proper Tool is purpose-built: a UK cost library covering flat and pitched roofing materials and labour, scaffold prelim tracking, AI quote generation from a description or site photos, and branded PDF export. Plans start at £29/month with a 14-day free trial offering one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, with no credit card required.",
    },
    {
      q: "Does the roofing quoting software cover both flat and pitched roofs?",
      a: "Yes. The Proper Tool covers pitched re-roofs (slate, concrete tile, clay tile), flat roofs (GRP, EPDM, felt, warm and cold deck), ridge and hip replacement, valley repair, lead flashings, fascia and soffit, and guttering. The AI generates the correct scope for each roof type from your description.",
    },
    {
      q: "Does roofer estimating software include scaffold costs?",
      a: "Yes. The Proper Tool automatically includes a scaffold hire and erection allowance on roofing jobs that require access equipment. You can adjust the amount or remove it if the job uses a tower or MEWP, but the line is there by default so it is never forgotten.",
    },
    {
      q: "How does roofing quoting software handle material price volatility?",
      a: "The Proper Tool carries current UK market rates for all common roofing materials (slate, concrete tile, GRP, EPDM, felt, lead). You can override any material rate with your own current supplier price, so the quote reflects what you're actually paying at the merchant rather than a generic national average.",
    },
    {
      q: "Is there a free trial for roofer quoting software?",
      a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start at £29/month.",
    },
  ],
  ctaHeading: "Ready to quote faster?",
  ctaBody:
    "Join roofers using The Proper Tool to generate professional quotes in minutes and win more jobs.",
  relatedLinks: [
    { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
    { href: "/quoting-software-for-electricians", label: "Quoting Software for Electricians" },
    { href: "/quoting-software-for-plumbers", label: "Quoting Software for Plumbers" },
    { href: "/ai-quoting-software", label: "AI Quoting Software for UK Builders" },
    { href: "/auto-quote", label: "Auto Quote: under 10 minutes" },
    { href: "/pricing", label: "Pricing & Plans" },
  ],
  relatedTrades: [
    {
      href: "/quoting-software-for-electricians",
      label: "Quoting software for electricians",
      description: "First fix, second fix, consumer units, and compliance notes. Full electrical scopes in under 10 minutes.",
    },
    {
      href: "/quoting-software-for-plumbers",
      label: "Quoting software for plumbers",
      description: "Bathroom fits, boiler swaps, heating systems, and drainage. Fully itemised plumbing quotes in under 10 minutes.",
    },
    {
      href: "/tradesman-quoting-tool",
      label: "Tradesman quoting tool",
      description: "The complete quoting toolkit for UK tradespeople across all trades. One platform, every job type.",
    },
  ],
};

const CARPENTERS: TradePageData = {
  slug: "quoting-software-for-carpenters",
  tradeName: "carpenters",
  breadcrumbLabel: "Quoting Software for Carpenters",
  seoTitle: "Quoting Software for Carpenters UK | The Proper Tool",
  seoDescription:
    "Quoting software built for carpenters in the UK. Generate accurate, itemised carpentry quotes covering first fix, second fix, joinery, staircases, and timber frame in under 10 minutes. From £29/mo.",
  schemaName: "The Proper Tool | Quoting Software for Carpenters",
  schemaDescription:
    "Quoting and estimating software built for carpenters in the UK. Generate accurate, professional quotes from drawings, photos or a description in under 10 minutes.",
  h1: "Quoting software for carpenters UK",
  heroSubtitle:
    "First fix, second fix, bespoke joinery, staircases, timber frame: The Proper Tool generates a fully itemised carpentry quote in under 10 minutes so you can price more jobs without spending evenings on spreadsheets.",
  heroSubtitle2:
    "Every line item priced from a UK cost library with rate bands you can override with your own labour rate and timber supplier prices.",
  painPointsHeading: "The carpenter's quoting problem",
  painPoints: [
    {
      heading: "First and second fix are priced completely differently",
      body: "First fix structural work (wall plates, joists, studwork, roof structure) has nothing in common with second fix finish work: architraves, skirtings, doors, and staircases. A single spreadsheet quote that lumps them together loses money. The Proper Tool prices each phase separately from a UK cost library built around how carpenters actually work.",
    },
    {
      heading: "Bespoke joinery is almost impossible to price upfront",
      body: "Fitted wardrobes, alcove units, and bespoke staircases depend on precise dimensions and finish spec. Clients expect a number before you've taken a single measurement. The Proper Tool lets you price from a description and flag bespoke elements as provisional sums, so you give a credible figure without committing to a scope you haven't fully measured.",
    },
    {
      heading: "Timber prices move constantly",
      body: "Structural timber, sheet materials, and hardwood prices fluctuate with supply chains. Under-price materials on a frame or floor and the margin disappears before the job is done. The Proper Tool carries current UK market rates and lets you override any rate with your current merchant price.",
    },
    {
      heading: "Quote writing takes evenings you don't have",
      body: "Most carpenters price jobs at home after a full day on site. The Proper Tool cuts that from two hours to under ten minutes, so you can send a professional PDF from your phone before you've left the property.",
    },
  ],
  featuresHeading: "Everything a carpenter needs to quote faster",
  featuresSubheading:
    "Built for UK carpentry: first and second fix, joinery, timber frame, staircases, and bespoke fit-out.",
  features: [
    {
      icon: Zap,
      title: "Full carpentry scope in under 10 minutes",
      description:
        "Describe the job (first fix frame, second fix finish, staircase, bespoke joinery) and the AI generates a fully itemised scope with UK market rates for every line item.",
    },
    {
      icon: BookOpen,
      title: "First and second fix cost library",
      description:
        "Structural timber, wall plates, joists, studwork, roof carpentry, doors, architraves, skirtings, staircases, and bespoke joinery allowances: all priced with UK Low, Typical, and High rate bands.",
    },
    {
      icon: PoundSterling,
      title: "Bespoke items as provisional sums",
      description:
        "Flag fitted wardrobes, alcove units, or complex staircases as provisional sums with a clear description. Clients understand what the allowance covers and you're not locked to a price before you've measured.",
    },
    {
      icon: FileText,
      title: "Itemised PDF your clients trust",
      description:
        "Every quote exports as a branded PDF with your company details, a full scope of works, and line-by-line pricing. Clients see exactly what the job involves with no round-number surprises.",
    },
    {
      icon: Smartphone,
      title: "Quote on-site from your phone",
      description:
        "Walk the site, describe what you see, and send a professional quote PDF before you've driven home. No laptop, no spreadsheet, no lost evenings.",
    },
    {
      icon: Wrench,
      title: "Override rates with your own",
      description:
        "Set your own labour rate and timber merchant prices on any item in the cost library. Your rates always take priority on future quotes so your margins stay protected.",
    },
  ],
  faqs: [
    {
      q: "What is the best quoting software for carpenters in the UK?",
      a: "For UK carpenters, The Proper Tool is purpose-built: a UK cost library covering first fix, second fix, and joinery trades, AI quote generation from a description or site photos, bespoke provisional sum support, and branded PDF export. Plans start at £29/month with a 14-day free trial offering one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, with no credit card required.",
    },
    {
      q: "Does carpentry quoting software cover both first fix and second fix?",
      a: "Yes. The Proper Tool covers first fix structural carpentry (wall plates, joists, studwork, roof structure, timber frame) and second fix finish carpentry (doors, architraves, skirtings, staircases, fitted furniture). The AI generates the correct scope for each phase from your description.",
    },
    {
      q: "How does carpenter estimating software handle bespoke joinery?",
      a: "Bespoke items like fitted wardrobes, alcove units, or complex staircases can be included as provisional sums with a clear description of what the allowance covers. Clients understand the scope and you are not committed to a fixed price before measuring. You can also override any line item with a specific amount once you have a supplier quote.",
    },
    {
      q: "How does carpentry quoting software handle timber price volatility?",
      a: "The Proper Tool carries current UK market rates for structural timber, sheet materials, and common hardwoods. You can override any rate with your own current merchant price so the quote reflects what you are actually paying rather than a generic national average.",
    },
    {
      q: "Is there a free trial for carpenter quoting software?",
      a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start at £29/month.",
    },
  ],
  ctaHeading: "Ready to quote faster?",
  ctaBody:
    "Join carpenters using The Proper Tool to generate professional quotes in minutes and win more jobs.",
  relatedLinks: [
    { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
    { href: "/quoting-software-for-plasterers", label: "Quoting Software for Plasterers" },
    { href: "/quoting-software-for-decorators", label: "Quoting Software for Decorators" },
    { href: "/quoting-software-for-electricians", label: "Quoting Software for Electricians" },
    { href: "/ai-quoting-software", label: "AI Quoting Software for UK Builders" },
    { href: "/pricing", label: "Pricing & Plans" },
  ],
  relatedTrades: [
    {
      href: "/quoting-software-for-plasterers",
      label: "Quoting software for plasterers",
      description: "Skimming, dot-and-dab, dry lining, and rendering. Fully itemised plastering quotes in under 10 minutes.",
    },
    {
      href: "/quoting-software-for-decorators",
      label: "Quoting software for decorators",
      description: "Interior and exterior decorating, wallpapering, and specialist coatings. Accurate decorator quotes fast.",
    },
    {
      href: "/tradesman-quoting-tool",
      label: "Tradesman quoting tool",
      description: "The complete quoting toolkit for UK tradespeople across all trades. One platform, every job type.",
    },
  ],
};

const PLASTERERS: TradePageData = {
  slug: "quoting-software-for-plasterers",
  tradeName: "plasterers",
  breadcrumbLabel: "Quoting Software for Plasterers",
  seoTitle: "Quoting Software for Plasterers UK | The Proper Tool",
  seoDescription:
    "Quoting software built for plasterers in the UK. Generate accurate, itemised plastering quotes covering dry lining, skimming, rendering, and beading in under 10 minutes. From £29/mo.",
  schemaName: "The Proper Tool | Quoting Software for Plasterers",
  schemaDescription:
    "Quoting and estimating software built for plasterers in the UK. Generate accurate, professional quotes from drawings, photos or a description in under 10 minutes.",
  h1: "Quoting software for plasterers UK",
  heroSubtitle:
    "Dry lining, skimming, rendering, beading: The Proper Tool generates a fully itemised plastering quote in under 10 minutes so you can price more jobs without spending evenings on spreadsheets.",
  heroSubtitle2:
    "Every m² priced from a UK cost library with Low, Typical, and High rate bands. Override any rate with your own.",
  painPointsHeading: "The plasterer's quoting problem",
  painPoints: [
    {
      heading: "Area measurement is time-consuming before you've got the job",
      body: "Skimming, rendering, and dry lining quotes depend on measured m² areas. Calculating wall and ceiling areas before you've won the job takes time you're not paid for. The Proper Tool lets you describe the rooms or take a photo of the drawings and generates a measured area estimate from which to build the scope.",
    },
    {
      heading: "Dry lining and wet plaster are priced differently",
      body: "A skim coat over existing plaster is a different scope from full dry lining on a new build or extension. Generic quoting tools don't understand the difference. The Proper Tool prices each type of plastering work with the correct UK rate: dot and dab boarding, tape and joint, multi-finish skim, sand and cement render.",
    },
    {
      heading: "Beading and prep are regularly under-quoted",
      body: "Stop bead, arch bead, and movement bead: easy to forget in a verbal quote and easy for the client not to notice until the bill arrives. The Proper Tool includes beading allowances when the scope calls for them so nothing is absorbed into your margin.",
    },
    {
      heading: "Quote writing takes evenings you don't have",
      body: "Most plasterers price jobs at home after a full day on the boards. The Proper Tool cuts that from two hours to under ten minutes, so you can send a professional PDF from your phone before you've left the site.",
    },
  ],
  featuresHeading: "Everything a plasterer needs to quote faster",
  featuresSubheading:
    "Built for UK plastering trades: dry lining, wet plaster, rendering, and external finish work.",
  features: [
    {
      icon: Zap,
      title: "Full plastering scope in under 10 minutes",
      description:
        "Describe the job (skim coat, dry lining, render, renovation) and the AI generates a fully itemised scope with UK market rates covering materials and labour for every line item.",
    },
    {
      icon: BookOpen,
      title: "Dry lining and wet plaster cost library",
      description:
        "Dot and dab boarding, tape and joint, multi-finish skim, sand and cement render, tyrolean, external monocouche: all priced with UK Low, Typical, and High rate bands.",
    },
    {
      icon: CheckCircle2,
      title: "Beading and prep included automatically",
      description:
        "Stop bead, arch bead, movement bead, bonding coat, and PVA priming: the AI includes prep and bead allowances when the scope requires them so you never absorb them into your margin.",
    },
    {
      icon: FileText,
      title: "Itemised PDF your clients trust",
      description:
        "Every quote exports as a branded PDF with your company details, a full scope of works, and line-by-line pricing. Clients see exactly what they are paying for with no round-number surprises.",
    },
    {
      icon: Smartphone,
      title: "Quote on-site from your phone",
      description:
        "Walk the rooms, describe what you see or take a photo of the drawings, and send a professional quote PDF before you have driven home. No laptop, no spreadsheet, no lost evenings.",
    },
    {
      icon: Wrench,
      title: "Override rates with your own",
      description:
        "Set your own labour rate and material prices on any item in the cost library. Your rates always take priority on future quotes so your margins stay protected.",
    },
  ],
  faqs: [
    {
      q: "What is the best quoting software for plasterers in the UK?",
      a: "For UK plasterers, The Proper Tool is purpose-built: a UK cost library covering dry lining, wet plaster, rendering, and external finish trades, AI quote generation from a description or site photos, automatic beading and prep allowances, and branded PDF export. Plans start at £29/month with a 14-day free trial offering one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, with no credit card required.",
    },
    {
      q: "Does plastering quoting software cover both dry lining and wet plaster?",
      a: "Yes. The Proper Tool covers dry lining (dot and dab, tape and joint, metal stud partitions), wet plaster (multi-finish skim, bonding, browning), sand and cement render, tyrolean, and external monocouche render. The AI generates the correct scope and rate for each type from your description.",
    },
    {
      q: "How does the software handle beading and prep items?",
      a: "The Proper Tool includes stop bead, arch bead, movement bead, bonding coat, and PVA priming allowances automatically when the scope requires them. You review every line before sending so you can add or remove items, but the common prep items are included by default so they are never forgotten.",
    },
    {
      q: "Can plastering estimating software work from room dimensions?",
      a: "Yes. Describe the rooms (e.g. '4 bedroom extension, walls and ceilings, new build dry lining') and the AI generates an area estimate for the scope. You can override any quantity with your own measured figures before sending the quote.",
    },
    {
      q: "Is there a free trial for plasterer quoting software?",
      a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start at £29/month.",
    },
  ],
  ctaHeading: "Ready to quote faster?",
  ctaBody:
    "Join plasterers using The Proper Tool to generate professional quotes in minutes and win more jobs.",
  relatedLinks: [
    { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
    { href: "/quoting-software-for-carpenters", label: "Quoting Software for Carpenters" },
    { href: "/quoting-software-for-decorators", label: "Quoting Software for Decorators" },
    { href: "/quoting-software-for-roofers", label: "Quoting Software for Roofers" },
    { href: "/ai-quoting-software", label: "AI Quoting Software for UK Builders" },
    { href: "/pricing", label: "Pricing & Plans" },
  ],
  relatedTrades: [
    {
      href: "/quoting-software-for-carpenters",
      label: "Quoting software for carpenters",
      description: "First fix, second fix, bespoke joinery, and staircases. Fully itemised carpentry quotes in under 10 minutes.",
    },
    {
      href: "/quoting-software-for-decorators",
      label: "Quoting software for decorators",
      description: "Interior and exterior decorating, wallpapering, and specialist coatings. Accurate decorator quotes fast.",
    },
    {
      href: "/tradesman-quoting-tool",
      label: "Tradesman quoting tool",
      description: "The complete quoting toolkit for UK tradespeople across all trades. One platform, every job type.",
    },
  ],
};

const DECORATORS: TradePageData = {
  slug: "quoting-software-for-decorators",
  tradeName: "decorators",
  breadcrumbLabel: "Quoting Software for Decorators",
  seoTitle: "Quoting Software for Decorators UK | The Proper Tool",
  seoDescription:
    "Quoting software built for decorators in the UK. Generate accurate, itemised decorating quotes covering painting, wallpapering, prep, and exterior work in under 10 minutes. From £29/mo.",
  schemaName: "The Proper Tool | Quoting Software for Decorators",
  schemaDescription:
    "Quoting and estimating software built for decorators in the UK. Generate accurate, professional quotes from drawings, photos or a description in under 10 minutes.",
  h1: "Quoting software for decorators UK",
  heroSubtitle:
    "Interior painting, wallpapering, prep and prime, exterior masonry: The Proper Tool generates a fully itemised decorating quote in under 10 minutes so you can price more jobs without spending evenings on spreadsheets.",
  heroSubtitle2:
    "Every m² and every linear metre priced from a UK cost library with Low, Typical, and High rate bands. Override any rate with your own.",
  painPointsHeading: "The decorator's quoting problem",
  painPoints: [
    {
      heading: "Prep work is invisible until it's underpriced",
      body: "Filling, sanding, sealing, priming: prep is the largest variable in any decorating quote and the easiest for the client to overlook. If you don't price it explicitly, you absorb it. The Proper Tool prices prep stages as separate line items so the client understands what the job involves and you're not doing it for free.",
    },
    {
      heading: "Number of coats changes the whole scope",
      body: "One coat on a new plaster wall is a different scope from three coats on a colour change or a full exterior repaint. Giving a single price for 'painting and decorating' without specifying coats leaves you exposed. The Proper Tool prices by coat and lets you specify the spec, so there's no ambiguity when the job starts.",
    },
    {
      heading: "Wallpapering is hard to quote without a pattern repeat",
      body: "Pattern repeat waste on premium wallpapers can add 20–30% to material costs. Under-quote materials and you're buying extra rolls at your own expense. The Proper Tool includes a pattern repeat waste allowance on wallpapering items so the quote reflects the real material cost.",
    },
    {
      heading: "Exterior work has weather and access costs that are easy to miss",
      body: "External masonry painting, timber windows, and fascia work involve access (scaffolding or tower), weather-dependent scheduling, and preparation that can double the scope of an interior equivalent. The Proper Tool prompts for access and prep requirements on exterior items so nothing is forgotten.",
    },
  ],
  featuresHeading: "Everything a decorator needs to quote faster",
  featuresSubheading:
    "Built for UK decorating trades: interior painting, wallpapering, prep and prime, and exterior finish work.",
  features: [
    {
      icon: Zap,
      title: "Full decorating scope in under 10 minutes",
      description:
        "Describe the job (interior repaint, wallpaper hang, exterior masonry, new plaster seal and paint) and the AI generates a fully itemised scope with UK market rates for every line item.",
    },
    {
      icon: BookOpen,
      title: "Painting and wallpapering cost library",
      description:
        "Prep and prime, emulsion by coat, gloss woodwork, wallpaper hanging, exterior masonry paint, window and fascia painting: all priced with UK Low, Typical, and High rate bands.",
    },
    {
      icon: CheckCircle2,
      title: "Prep priced as separate line items",
      description:
        "Filling, sanding, sealing new plaster, mist coat, spot priming: prep stages are included as explicit line items so the client understands the full scope and you are not absorbing the cost into your day rate.",
    },
    {
      icon: FileText,
      title: "Itemised PDF your clients trust",
      description:
        "Every quote exports as a branded PDF with your company details, a full scope of works, coat counts, and line-by-line pricing. Clients see exactly what they are paying for with no round-number surprises.",
    },
    {
      icon: Smartphone,
      title: "Quote on-site from your phone",
      description:
        "Walk the rooms or the exterior, describe what you see, and send a professional quote PDF before you have left the property. No laptop, no spreadsheet, no lost evenings.",
    },
    {
      icon: Wrench,
      title: "Override rates with your own",
      description:
        "Set your own labour rate and paint supplier prices on any item in the cost library. Your rates always take priority on future quotes so your margins stay protected.",
    },
  ],
  faqs: [
    {
      q: "What is the best quoting software for decorators in the UK?",
      a: "For UK decorators, The Proper Tool is purpose-built: a UK cost library covering interior and exterior painting, wallpapering, and prep trades, AI quote generation from a description or site photos, explicit prep line items, and branded PDF export. Plans start at £29/month with a 14-day free trial offering one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, with no credit card required.",
    },
    {
      q: "Does decorator quoting software price prep work separately?",
      a: "Yes. The Proper Tool prices filling, sanding, sealing new plaster, mist coat, and priming as separate line items so the client can see the full scope and you are not absorbing prep costs into your day rate. Each prep stage is itemised in the PDF the client receives.",
    },
    {
      q: "How does decorating estimating software handle wallpaper pattern repeat waste?",
      a: "The Proper Tool includes a pattern repeat waste allowance on wallpapering line items so the material quantity reflects the real number of rolls you will need, including waste. You can adjust the waste percentage for any item if the specific wallpaper has an unusually large or small repeat.",
    },
    {
      q: "Does the software cover exterior painting and masonry work?",
      a: "Yes. The Proper Tool covers exterior masonry painting, timber window and door painting, fascia and soffit painting, and exterior render painting. Access costs (scaffold or tower allowance) and weather-sensitive prep requirements are included in the scope for exterior items.",
    },
    {
      q: "Is there a free trial for decorator quoting software?",
      a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start at £29/month.",
    },
  ],
  ctaHeading: "Ready to quote faster?",
  ctaBody:
    "Join decorators using The Proper Tool to generate professional quotes in minutes and win more jobs.",
  relatedLinks: [
    { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
    { href: "/quoting-software-for-carpenters", label: "Quoting Software for Carpenters" },
    { href: "/quoting-software-for-plasterers", label: "Quoting Software for Plasterers" },
    { href: "/quoting-software-for-electricians", label: "Quoting Software for Electricians" },
    { href: "/ai-quoting-software", label: "AI Quoting Software for UK Builders" },
    { href: "/pricing", label: "Pricing & Plans" },
  ],
  relatedTrades: [
    {
      href: "/quoting-software-for-carpenters",
      label: "Quoting software for carpenters",
      description: "First fix, second fix, bespoke joinery, and staircases. Fully itemised carpentry quotes in under 10 minutes.",
    },
    {
      href: "/quoting-software-for-plasterers",
      label: "Quoting software for plasterers",
      description: "Skimming, dot-and-dab, dry lining, and rendering. Fully itemised plastering quotes in under 10 minutes.",
    },
    {
      href: "/tradesman-quoting-tool",
      label: "Tradesman quoting tool",
      description: "The complete quoting toolkit for UK tradespeople across all trades. One platform, every job type.",
    },
  ],
};

export const TRADE_PAGES: Record<string, TradePageData> = {
  electricians: ELECTRICIANS,
  plumbers: PLUMBERS,
  roofers: ROOFERS,
  carpenters: CARPENTERS,
  plasterers: PLASTERERS,
  decorators: DECORATORS,
};

const PRICING_PLANS = [
  {
    name: "Lite",
    price: "£29",
    desc: "For sole traders",
    features: [
      "10 AI auto-quotes/mo",
      "Unlimited template & manual quotes",
      "Professional PDF export",
      "Full 31-category cost library",
    ],
    highlight: false,
  },
  {
    name: "Starter",
    price: "£59",
    desc: "For growing businesses",
    features: [
      "25 AI auto-quotes/mo",
      "Everything in Lite",
      "Pricing Intelligence",
      "Expert validation pipeline",
      "Regional pricing",
    ],
    highlight: true,
  },
  {
    name: "Pro",
    price: "£99",
    desc: "For high-volume tradespeople",
    features: [
      "50 AI auto-quotes/mo",
      "Everything in Starter",
      "Drawing analysis",
      "Learned rate adjustments",
    ],
    highlight: false,
  },
] as const;

interface TradeVerticalPageProps {
  trade: TradePageData;
}

function buildFaqSchema(trade: TradePageData) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": trade.faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a },
    })),
  };
}

function buildBreadcrumbSchema(trade: TradePageData) {
  const pageUrl = `https://thepropertool.com/${trade.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
      { "@type": "ListItem", "position": 2, "name": trade.breadcrumbLabel, "item": pageUrl },
    ],
  };
}

export function TradeVerticalPage({ trade }: TradeVerticalPageProps) {
  const pageUrl = `https://thepropertool.com/${trade.slug}`;

  const softwareSchema = {
    ...SOFTWARE_SCHEMA,
    "name": trade.schemaName,
    "description": trade.schemaDescription,
    "url": pageUrl,
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title={trade.seoTitle}
        description={trade.seoDescription}
        canonical={`/${trade.slug}`}
        schema={[softwareSchema, buildBreadcrumbSchema(trade), buildFaqSchema(trade), ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>{trade.breadcrumbLabel}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              {trade.h1}
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-4 max-w-3xl mx-auto">
              {trade.heroSubtitle}
            </p>
            {trade.heroSubtitle2 && (
              <p className="text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
                {trade.heroSubtitle2}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/ai-quoting-software">How Smart Quoting works</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* Pain points */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10">{trade.painPointsHeading}</h2>
            <div className="space-y-8">
              {trade.painPoints.map((point) => (
                <div key={point.heading} className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">{point.heading}</h3>
                    <p className="text-muted-foreground leading-relaxed">{point.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="how-it-works">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">How it works</h2>
              <p className="text-lg text-muted-foreground">Three steps from job description to client-ready quote.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              {[
                {
                  step: "1",
                  title: "Describe the job or take a photo",
                  description:
                    "Type what needs doing in plain language, take a photo on-site, or upload any drawings you have. Works from a few lines of text if that's all you've got.",
                },
                {
                  step: "2",
                  title: "AI generates your itemised quote",
                  description: `The AI reads your inputs and produces a fully itemised scope of works for the ${trade.tradeName} job: every line item, quantities, and UK rates applied. Takes under 10 minutes.`,
                },
                {
                  step: "3",
                  title: "Review, adjust, and send the PDF",
                  description:
                    "Check every line, add or remove items, adjust any price you want, then export a branded PDF and send it to the client.",
                },
              ].map((s) => (
                <div key={s.step} className="relative">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4">
                    {s.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="features">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">{trade.featuresHeading}</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{trade.featuresSubheading}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {trade.features.map((f) => (
                <div key={f.title} className="bg-background rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-primary mb-2">10 min</p>
                <p className="text-muted-foreground">Average time to generate a full itemised quote</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">31</p>
                <p className="text-muted-foreground">UK trade categories covered in a single quote</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">£29/mo</p>
                <p className="text-muted-foreground">Starting price, less than one hour of your time</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple pricing for tradespeople</h2>
              <p className="text-lg text-muted-foreground">Start with a 14-day free trial. No credit card required.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {PRICING_PLANS.map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">
                      {plan.price}
                      <span className="text-base font-normal text-muted-foreground">/mo</span>
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {trade.faqs.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Also covers — trade cross-links */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8">Also covers</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {trade.relatedTrades.map((rt) => (
                <Link
                  key={rt.href}
                  href={rt.href}
                  className="group block rounded-xl border border-border bg-background p-5 hover:border-primary/50 hover:shadow-sm transition-all"
                >
                  <p className="font-semibold text-base mb-2 group-hover:text-primary transition-colors capitalize">
                    {rt.label}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{rt.description}</p>
                  <span className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-3">
                    Learn more <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related pages */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {trade.relatedLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{trade.ctaHeading}</h2>
            <p className="text-lg text-muted-foreground mb-8">{trade.ctaBody}</p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}

export function ElectriciansPage() {
  return <TradeVerticalPage trade={TRADE_PAGES.electricians} />;
}

export function PlumbersPage() {
  return <TradeVerticalPage trade={TRADE_PAGES.plumbers} />;
}

export function RoofersPage() {
  return <TradeVerticalPage trade={TRADE_PAGES.roofers} />;
}

export function CarpentersPage() {
  return <TradeVerticalPage trade={TRADE_PAGES.carpenters} />;
}

export function PlasterersPage() {
  return <TradeVerticalPage trade={TRADE_PAGES.plasterers} />;
}

export function DecoratorsPage() {
  return <TradeVerticalPage trade={TRADE_PAGES.decorators} />;
}
